// Razorpay Checkout helper — opens the modal, verifies signature server-side, redirects to result page.
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";

const waitForRazorpay = (ms = 4000) =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const t0 = Date.now();
    const iv = setInterval(() => {
      if (window.Razorpay) { clearInterval(iv); resolve(true); }
      else if (Date.now() - t0 > ms) { clearInterval(iv); resolve(false); }
    }, 80);
  });

/**
 * Open Razorpay Checkout for either a subscription (lookup_key) or an invoice (invoice_id).
 * On successful payment we call POST /payments/verify to validate the signature server-side,
 * then redirect to /payment/success?session_id={order_id} for confirmation UI.
 */
export async function openRazorpayCheckout({ lookup_key, invoice_id } = {}) {
  try {
    const ready = await waitForRazorpay();
    if (!ready) {
      toast.error("Payment system not loaded. Please refresh and try again.");
      return;
    }

    const { data } = await api.post("/payments/checkout", {
      lookup_key,
      invoice_id,
      origin_url: window.location.origin,
    });

    if (data.provider !== "razorpay") {
      // Legacy Stripe fallback — redirect to hosted checkout
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.error("Payment provider misconfigured");
      return;
    }

    const rzp = new window.Razorpay({
      key: data.key_id,
      amount: data.amount,
      currency: data.currency,
      name: data.name,
      description: data.description,
      order_id: data.order_id,
      prefill: data.prefill || {},
      notes: data.notes || {},
      theme: { color: "#1A362B" }, // Viyora Deep Pine
      handler: async (response) => {
        try {
          await api.post("/payments/verify", {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          window.location.href = `/payment/success?session_id=${response.razorpay_order_id}`;
        } catch (err) {
          toast.error(apiError(err, "Verification failed. Contact support."));
        }
      },
      modal: {
        ondismiss: () => {
          toast.info("Checkout dismissed. Your card wasn't charged.");
        },
      },
    });

    rzp.on("payment.failed", (resp) => {
      const reason = resp?.error?.description || "Payment failed";
      toast.error(reason);
    });

    rzp.open();
  } catch (err) {
    toast.error(apiError(err, "Payment init failed"));
  }
}
